/*
 * rheincool — Wartungs-Planer (Wartung-Handoff, MaintenancePlanner)
 * intervall = Regelwerk[typ][nutzung] (Monate, Section-Settings);
 * fällig = letzte Wartung + intervall; Preis = Grundpreis(typ)
 * + Aufschlag je zusätzlichem Multisplit-Innengerät.
 * Publiziert { status, intervall, preisAb } unter Store-Key 'maintenance'.
 * CTA erhält die Planer-Daten als Query-Params (weniger Tipparbeit im
 * Anfrage-Formular): geraetetyp, geraeteanzahl, nutzung, letzte_wartung, status.
 */
class RcMaintenancePlanner extends window.RcCalculator {
  init() {
    this.storeKey = 'maintenance';
    this.trackPrefix = 'planner';
    this.overdueTracked = false;
    this.countGroup = this.querySelector('[data-mp-count]');
    this.lastSelect = this.querySelector('[data-mp-last]');
    this.intervalEl = this.querySelector('[data-mp-interval]');
    this.statusEl = this.querySelector('[data-mp-status]');
    this.cleanEl = this.querySelector('[data-mp-clean]');
    this.priceEl = this.querySelector('[data-mp-price]');
    this.stickyValue = this.querySelector('[data-mp-sticky-value]');
    this.ctas = this.querySelectorAll('[data-mp-cta]');

    /* Geräteanzahl sofort (nicht erst on blur) übernehmen */
    const countField = this.querySelector('[data-calc-field="anzahl"]');
    if (countField) countField.addEventListener('input', () => this.update());
  }

  trackParams() {
    return {
      typ: this.segmentValue('typ'),
      nutzung: this.segmentValue('nutzung'),
      letzte_wartung: this.lastSelect ? this.lastSelect.value : '',
    };
  }

  fill(template, params) {
    return Object.entries(params).reduce(
      (text, [key, value]) => text.replaceAll(`{${key}}`, value),
      template || ''
    );
  }

  monthsSince(lastValue) {
    switch (lastValue) {
      case 'dieses-jahr':
        return 0;
      case 'vor-1-jahr':
        return 12;
      case 'vor-2-jahren':
        return 24;
      case 'vor-3-jahren':
        return 36;
      default:
        return null; /* noch nie */
    }
  }

  compute() {
    const typ = this.segmentValue('typ');
    const nutzung = this.segmentValue('nutzung');
    const letzte = this.lastSelect ? this.lastSelect.value : '';

    /* Anzahl-Feld nur bei Multisplit zeigen */
    if (this.countGroup) this.countGroup.hidden = typ !== 'multisplit';
    if (!typ || !nutzung || !letzte) return null;

    const intervall = this.config.intervalle?.[typ]?.[nutzung];
    if (!intervall) return null;

    let anzahl = 1;
    if (typ === 'multisplit') {
      anzahl = Math.min(5, Math.max(2, this.fieldValue('anzahl') || 2));
    }

    const grundpreis = this.config.grundpreis?.[typ] || 0;
    const aufschlag = typ === 'multisplit' ? (anzahl - 1) * (this.config.aufschlagInnengeraet || 0) : 0;
    const preisAb = grundpreis + aufschlag;

    const vergangen = this.monthsSince(letzte);
    const neverServiced = vergangen === null;
    const restMonate = neverServiced ? -1 : intervall - vergangen;
    const status = restMonate <= 0 ? 'ueberfaellig' : 'faellig';

    let faelligMonat = '';
    if (status === 'faellig') {
      const due = new Date();
      due.setMonth(due.getMonth() + restMonate);
      faelligMonat = new Intl.DateTimeFormat('de-DE', { month: 'long', year: 'numeric' }).format(due);
    }

    return {
      status,
      intervall,
      preisAb,
      typ,
      nutzung,
      letzte,
      anzahl,
      neverServiced,
      faelligMonat,
    };
  }

  renderResult(result) {
    const i18n = this.config.i18n || {};

    if (this.intervalEl) {
      this.intervalEl.textContent = this.fill(i18n.intervalValue, {
        monate: this.formatNumber(result.intervall),
      });
    }

    /* Status-Badge: überfällig IMMER Icon + Text (nie nur Farbe) */
    if (this.statusEl) {
      this.statusEl.classList.toggle('rc-mplanner__badge--overdue', result.status === 'ueberfaellig');
      this.statusEl.classList.toggle('rc-mplanner__badge--neutral', result.status !== 'ueberfaellig');
      if (result.status === 'ueberfaellig') {
        this.statusEl.textContent = '';
        const icon = document.createElement('span');
        icon.className = 'rc-mplanner__badge-icon';
        icon.setAttribute('aria-hidden', 'true');
        icon.textContent = '⚠';
        this.statusEl.append(icon, ` ${i18n.overdueLabel || ''}`);
      } else {
        this.statusEl.textContent = this.fill(i18n.dueLabel, { monat: result.faelligMonat });
      }
    }

    if (this.cleanEl) this.cleanEl.hidden = !result.neverServiced;

    const preisText = this.fill(i18n.priceLabel, { preis: `${this.formatNumber(result.preisAb)},– €` });
    if (this.priceEl) this.priceEl.textContent = preisText;
    if (this.stickyValue) this.stickyValue.textContent = preisText;

    /* Planer-Daten an die Wartungsanfrage übergeben */
    this.ctas.forEach((cta) => {
      try {
        const url = new URL(cta.getAttribute('href'), window.location.origin);
        url.searchParams.set('geraetetyp', result.typ);
        url.searchParams.set('geraeteanzahl', String(result.anzahl));
        url.searchParams.set('nutzung', result.nutzung);
        url.searchParams.set('letzte_wartung', result.letzte);
        url.searchParams.set('status', result.status);
        cta.setAttribute('href', url.pathname + url.search + url.hash);
        cta.dataset.rcTrackStatus = result.status;
      } catch {
        /* relative URL ohne Basis — CTA unverändert lassen */
      }
    });

    if (result.status === 'ueberfaellig') {
      if (!this.overdueTracked) {
        this.overdueTracked = true;
        this.track('planner_overdue_shown', { typ: result.typ, nutzung: result.nutzung });
      }
      this.announce(this.fill(i18n.announceOverdue, { preis: preisText }));
    } else {
      this.announce(
        this.fill(i18n.announceDue, {
          monat: result.faelligMonat,
          monate: this.formatNumber(result.intervall),
          preis: preisText,
        })
      );
    }
  }
}

if (!customElements.get('rc-maintenance-planner')) {
  customElements.define('rc-maintenance-planner', RcMaintenancePlanner);
}
